import type {
  FileListOrdering,
  FileSource,
  FileType,
} from "@/react/components/entityViews/files/types/swivuFile";

/**
 * Centralised query-key factory for the files feature.
 *
 * Key hierarchy (each level is a prefix-match superset of its children):
 *   all                               — invalidate every file query on the board
 *   board(boardId)                    — all files on a board
 *   entity(boardId, type, id)         — all queries for one entity (ignores filters)
 *   entityFiltered(boardId, type, id, filters) — exact query used by useEntityFiles
 */

export interface FileListFilters {
  source?: FileSource;
  fileType?: FileType;
  search?: string;
  /** Inclusive lower bound (ISO-8601) for `uploaded_at` */
  uploadedAfter?: string;
  /** Inclusive upper bound (ISO-8601) for `uploaded_at` */
  uploadedBefore?: string;
  /** Server ordering; default when omitted is `-uploaded_at` */
  ordering?: FileListOrdering;
}

export const fileKeys = {
  all: ["files"] as const,

  board: (boardId: number) => [...fileKeys.all, boardId] as const,

  entity: (boardId: number, entityType: string, entityId: number) =>
    [...fileKeys.board(boardId), entityType, entityId] as const,

  entityFiltered: (
    boardId: number,
    entityType: string,
    entityId: number,
    filters: FileListFilters
  ) => [...fileKeys.entity(boardId, entityType, entityId), filters] as const,

  /**
   * URL resolution for a single file by ID.
   * Kept under the "files" namespace so a broad invalidation clears it too.
   */
  fileUrl: (fileId: number) => [...fileKeys.all, "url", fileId] as const,
} as const;
